import { Children, useState, useRef, useCallback, useEffect, cloneElement } from 'react';
import { IconNames } from '@shipengine/giger-theme';
import { getNavStyles, getChevronNavStyles, getTabListStyles } from './TabListStyles.js';
import { jsxs, jsx } from '@emotion/react/jsx-runtime';
import { Page } from '../Page/Page.js';
import { Icon } from '../Icon/Icon.js';

var NavDirection = /*#__PURE__*/function (NavDirection) {
  NavDirection["RIGHT"] = "right";
  NavDirection["LEFT"] = "left";
  return NavDirection;
}(NavDirection || {}); // Number of pixels navigation will move when chevrons are clicked
const NUMBER_PIXELS = 180;
const TabList = _ref => {
  let {
    children,
    ...rest
  } = _ref;
  const childrenArray = Children.toArray(children);
  const [chevronRightVisible, setChevronRightVisible] = useState(false);
  const [chevronRightDisabled, setChevronRightDisabled] = useState(false);
  const [chevronLeftVisible, setChevronLeftVisible] = useState(false);
  const [chevronLeftDisabled, setChevronLeftDisabled] = useState(false);
  const navRef = useRef(null);
  const listRef = useRef(null);
  const onScroll = useCallback(() => {
    if (listRef.current) {
      const contentWidth = listRef.current.scrollWidth - listRef.current.offsetWidth;
      setChevronLeftDisabled(listRef.current.scrollLeft === 0);
      setChevronRightDisabled(contentWidth <= listRef.current.scrollLeft + 1);
    }
  }, []);
  useEffect(() => {
    if (listRef.current && navRef.current) {
      if (navRef.current.offsetWidth < listRef.current.scrollWidth) {
        setChevronRightVisible(true);
        setChevronLeftVisible(true);
      }
      onScroll();
    }
  }, [onScroll]);
  const handleNav = direction => {
    if (direction === NavDirection.RIGHT && listRef.current) {
      listRef.current.scrollLeft += NUMBER_PIXELS;
    } else if (direction === NavDirection.LEFT && listRef.current) {
      listRef.current.scrollLeft -= NUMBER_PIXELS;
    }
  };
  const handleTabClick = event => {
    var _listRef$current, _listRef$current2, _listRef$current3;
    if (!listRef.current) {
      return;
    }
    const tabClicked = event.target;
    const listContainerOffsetLeft = (_listRef$current = listRef.current) === null || _listRef$current === void 0 ? void 0 : _listRef$current.offsetLeft;
    const listContainerScrollLeft = (_listRef$current2 = listRef.current) === null || _listRef$current2 === void 0 ? void 0 : _listRef$current2.scrollLeft;
    const listContainerWidth = (_listRef$current3 = listRef.current) === null || _listRef$current3 === void 0 ? void 0 : _listRef$current3.offsetWidth;
    const tabWidth = tabClicked.offsetWidth;
    const tabOffsetLeft = tabClicked.offsetLeft;
    const spaceNeededToShowTab = tabWidth + (tabOffsetLeft - listContainerOffsetLeft);
    const spaceAvailableInTabContainer = listContainerWidth + listContainerScrollLeft;
    const tabOnLeftNeedsMoreSpace = listContainerScrollLeft > tabOffsetLeft - listContainerOffsetLeft;
    if (spaceNeededToShowTab > spaceAvailableInTabContainer || tabOnLeftNeedsMoreSpace) {
      listRef.current.scrollLeft = tabOffsetLeft - listContainerOffsetLeft;
    }
  };
  return jsxs("nav", {
    "aria-label": "tabulation",
    css: getNavStyles,
    ref: navRef,
    ...rest,
    children: [chevronLeftVisible && jsx(Page, {
      withIcon: true,
      disabled: chevronLeftDisabled,
      ariaLabel: "chevron-left",
      selectable: false,
      onClick: () => handleNav(NavDirection.LEFT),
      children: jsx(Icon, {
        css: getChevronNavStyles(!chevronLeftDisabled),
        name: IconNames.CHEVRON_LEFT
      })
    }), jsx("div", {
      ref: listRef,
      role: "tablist",
      css: getTabListStyles,
      "aria-label": "tabs-list",
      itemScope: true,
      itemType: "https://schema.org/ItemList",
      onScroll: onScroll,
      children: childrenArray.map((child, index) => {
        const tabListChild = child;
        const tabListChildProps = tabListChild.props;
        return /*#__PURE__*/cloneElement(tabListChild, {
          key: (tabListChildProps === null || tabListChildProps === void 0 ? void 0 : tabListChildProps.tabId) || index,
          itemScope: true,
          itemProp: 'itemListElement',
          'aria-label': 'tabs-listItem',
          itemType: 'https://schema.org/ListItem',
          'data-id': tabListChildProps['data-id'] || 'tabs-listItem',
          onClick: (selectedItem, event) => {
            var _tabListChildProps$on;
            handleTabClick(event);
            (_tabListChildProps$on = tabListChildProps.onClick) === null || _tabListChildProps$on === void 0 ? void 0 : _tabListChildProps$on.call(tabListChildProps, selectedItem, event);
          }
        });
      })
    }), chevronRightVisible && jsx(Page, {
      withIcon: true,
      disabled: chevronRightDisabled,
      ariaLabel: "chevron-right",
      selectable: false,
      onClick: () => handleNav(NavDirection.RIGHT),
      children: jsx(Icon, {
        css: getChevronNavStyles(!chevronRightDisabled),
        name: IconNames.CHEVRON_RIGHT
      })
    })]
  });
};

export { TabList };
//# sourceMappingURL=TabList.js.map
